/**
 * Organization Management Prototype
 * Org switcher, toggle on/off, create/manage orgs, scope indicator.
 *
 * Access: http://flownotes.localhost/?test=organization-management
 *
 * Reference: docs/design/2026-02-20-organization-management-wireframes.md
 * Spec: docs/implementations/core/organization-management/specs/2026-02-20-organization-management-spec.md
 */

import { useState } from 'react';
import {
  ArrowLeft,
  Plus,
  Building2,
  ChevronDown,
  ChevronRight,
  Settings as SettingsIcon,
  Search,
  User,
} from 'lucide-react';
import { WORKSPACE_ICONS, getWorkspaceIcon } from '@/lib/workspace-icons';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';

const ORG_ICONS = WORKSPACE_ICONS;

const ORG_COLORS = [
  { value: '#3b82f6', label: 'Blue' },
  { value: '#22c55e', label: 'Green' },
  { value: '#f59e0b', label: 'Amber' },
  { value: '#ef4444', label: 'Red' },
  { value: '#a855f7', label: 'Purple' },
  { value: '#14b8a6', label: 'Teal' },
  { value: '#64748b', label: 'Slate' },
  { value: '#f43f5e', label: 'Rose' },
];

function hexToRgba(hex: string, alpha: number): string {
  const r = parseInt(hex.slice(1, 3), 16);
  const g = parseInt(hex.slice(3, 5), 16);
  const b = parseInt(hex.slice(5, 7), 16);
  return `rgba(${r}, ${g}, ${b}, ${alpha})`;
}

/** Schema-aligned org type: docs/implementations/core/organization-management/schema.md */
type OrgType = 'personal' | 'work' | 'client' | 'community' | 'other';

interface MockOrg {
  id: string;
  name: string;
  type: OrgType;
  enabled: boolean;
  icon?: string;
  color?: string;
  role?: string;
  memberCount?: number;
}

const ORG_TYPES: { value: OrgType; label: string }[] = [
  { value: 'personal', label: 'Personal' },
  { value: 'work', label: 'Work' },
  { value: 'client', label: 'Client' },
  { value: 'community', label: 'Community' },
  { value: 'other', label: 'Other' },
];

function OrgIcon({ org, className = 'h-4 w-4 shrink-0' }: { org: MockOrg; className?: string }) {
  const Icon = getWorkspaceIcon(org.icon, org.type === 'personal' ? User : Building2);
  return <Icon className={className} />;
}

function OrgIndicator({ org }: { org: MockOrg }) {
  const Icon = getWorkspaceIcon(org.icon, org.type === 'personal' ? User : Building2);
  const hasColor = !!org.color;
  return (
    <div className="relative w-8 h-6 shrink-0 flex items-center justify-center">
      {hasColor ? (
        <div
          className={`absolute inset-0 rounded-md border border-border transition-opacity ${!org.enabled ? 'opacity-20' : ''}`}
          style={{ backgroundColor: hexToRgba(org.color!, 0.5) }}
        />
      ) : (
        <div className={`absolute inset-0 rounded-md bg-muted transition-opacity ${!org.enabled ? 'opacity-20' : ''}`} />
      )}
      <Icon
        className={`relative z-10 h-5 w-5 ${hasColor ? 'text-white drop-shadow-sm' : 'text-foreground'}`}
      />
    </div>
  );
}

// Mock data for prototype (no backend) - schema: type, icon, color
const initialOrgs: MockOrg[] = [
  { id: 'personal', name: 'Personal', type: 'personal', enabled: true, icon: 'User', color: '#64748b' },
  { id: 'hop', name: 'Hop Studios', type: 'work', enabled: true, icon: 'Building2', color: '#3b82f6', role: 'Admin', memberCount: 3 },
  { id: 'acme', name: 'Acme Corp', type: 'client', enabled: false, icon: 'Briefcase', color: '#22c55e', role: 'Member', memberCount: 5 },
];

const mockPendingInvite = { id: 'invite1', orgName: 'Design Co', email: 'you@example.com' };

export function TestOrganizationManagement() {
  const [orgs, setOrgs] = useState<MockOrg[]>(initialOrgs);
  const [settingsOpen, setSettingsOpen] = useState(false);
  const [settingsTab, setSettingsTab] = useState<string>('account');
  const [settingsOrgId, setSettingsOrgId] = useState<string | null>(null);
  const [newOrgName, setNewOrgName] = useState('');
  const [newOrgSlug, setNewOrgSlug] = useState('');
  const [newOrgType, setNewOrgType] = useState<OrgType>('work');
  const [newOrgIcon, setNewOrgIcon] = useState('Building2');
  const [newOrgColor, setNewOrgColor] = useState(ORG_COLORS[0].value);

  const openSettingsToOrganizations = () => {
    setSettingsTab('organizations');
    setSettingsOpen(true);
  };

  const enabledOrgs = orgs.filter((o) => o.enabled);
  const scopeLabel = enabledOrgs.length === 0
    ? 'No orgs enabled'
    : enabledOrgs.map((o) => o.name).join(', ');

  const toggleOrg = (id: string) => {
    setOrgs((prev) =>
      prev.map((o) => (o.id === id ? { ...o, enabled: !o.enabled } : o))
    );
  };

  const prepareCreateForm = () => {
    setNewOrgName('');
    setNewOrgSlug('');
    setNewOrgType('work');
    setNewOrgIcon('Building2');
    setNewOrgColor(ORG_COLORS[0].value);
  };

  const handleCreateOrg = () => {
    if (!newOrgName.trim()) return;
    const slug = newOrgSlug || newOrgName.toLowerCase().replace(/\s+/g, '-');
    setOrgs((prev) => [
      ...prev,
      {
        id: slug,
        name: newOrgName.trim(),
        type: newOrgType,
        enabled: true,
        icon: newOrgIcon,
        color: newOrgColor,
        role: 'Admin',
        memberCount: 1,
      },
    ]);
    prepareCreateForm();
    setSettingsOrgId(null);
  };

  const updateOrgField = (orgId: string, field: 'type' | 'icon' | 'color', value: string) => {
    setOrgs((prev) =>
      prev.map((o) =>
        o.id === orgId ? { ...o, [field]: o.type === 'personal' && field === 'type' ? 'personal' : value } : o
      )
    );
  };

  return (
    <div className="h-screen flex flex-col bg-background">
      {/* Mock header: mimics app header with org switcher */}
      <div className="border-b px-4 h-12 flex items-center justify-between bg-background">
        <div className="flex items-center gap-4">
          <Button variant="ghost" size="sm" asChild>
            <a href="/?test=index">
              <ArrowLeft className="w-4 h-4 mr-1" />
              Prototypes
            </a>
          </Button>
          <span className="text-muted-foreground">|</span>
          <span className="text-sm text-muted-foreground">Org switcher (expandable):</span>
        </div>
        <div className="flex items-center gap-2">
          <Button
            variant="ghost"
            size="icon"
            onClick={openSettingsToOrganizations}
            title="Settings"
          >
            <SettingsIcon className="h-4 w-4" />
          </Button>
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button variant="outline" size="sm" className="gap-2 min-w-[120px]">
                {enabledOrgs.length === 1 ? (
                  <OrgIcon org={enabledOrgs[0]} />
                ) : (
                  <Building2 className="h-4 w-4 shrink-0" />
                )}
                {enabledOrgs.length === 1 ? (
                  <span className="truncate">{enabledOrgs[0].name}</span>
                ) : (
                  <Badge variant="secondary" className="h-5 min-w-5 px-1.5 font-normal">
                    {enabledOrgs.length}
                  </Badge>
                )}
                <ChevronDown className="h-4 w-4 shrink-0 opacity-50" />
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" className="w-56">
              <div className="px-2 py-2 space-y-1">
                <p className="text-xs text-muted-foreground mb-2">Toggle orgs for this session</p>
                {orgs.map((org) => (
                  <div
                    key={org.id}
                    className={`flex items-center gap-2 px-2 py-1.5 rounded-md cursor-pointer transition-colors outline-none focus:outline-none focus-visible:ring-0 active:ring-0 select-none ${
                      org.enabled
                        ? 'text-foreground font-medium'
                        : 'text-muted-foreground/50 hover:bg-accent/30 hover:text-muted-foreground/80'
                    } ${org.enabled && !org.color ? 'bg-primary/20' : ''}`}
                    style={org.enabled && org.color ? { backgroundColor: hexToRgba(org.color, 0.2) } : undefined}
                    onClick={() => toggleOrg(org.id)}
                  >
                    <OrgIndicator org={org} />
                    <span className="text-sm">{org.name}</span>
                  </div>
                ))}
              </div>
              <DropdownMenuSeparator />
              <DropdownMenuItem onClick={openSettingsToOrganizations}>
                <SettingsIcon className="h-4 w-4 mr-2" />
                Manage workspaces...
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
          <span className="text-xs text-muted-foreground ml-2 max-w-[200px] truncate" title={scopeLabel}>
            Scope: {scopeLabel}
          </span>
        </div>
      </div>

      <div className="flex-1 overflow-auto p-6">
        <div className="max-w-2xl mx-auto space-y-8">
          {/* Flat bar variant */}
          <Card>
            <CardHeader>
              <CardTitle className="text-base">Alternate: Flat org chips</CardTitle>
              <CardDescription>
                Simpler variant with chips always visible. Header above uses expandable menu (preferred).
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="flex flex-wrap gap-2 p-3 rounded-lg bg-muted/50 border">
                {orgs.map((org) => (
                  <div
                    key={org.id}
                    className={`flex items-center gap-2 px-3 py-1.5 rounded-md border cursor-pointer transition-colors outline-none focus:outline-none focus-visible:ring-0 active:ring-0 select-none ${
                      org.enabled
                        ? 'border-transparent text-foreground font-medium'
                        : 'bg-background border-border text-muted-foreground/50 hover:border-primary/20 hover:text-muted-foreground/80'
                    } ${org.enabled && !org.color ? 'bg-primary/20' : ''}`}
                    style={org.enabled && org.color ? { backgroundColor: hexToRgba(org.color, 0.2) } : undefined}
                    onClick={() => toggleOrg(org.id)}
                  >
                    <OrgIndicator org={org} />
                    <span className="text-sm font-medium">{org.name}</span>
                  </div>
                ))}
              </div>
              <div className="text-sm text-muted-foreground">
                <strong>Scope:</strong> {scopeLabel}
              </div>
            </CardContent>
          </Card>

          {/* Settings modal */}
          <Dialog open={settingsOpen} onOpenChange={(open) => { setSettingsOpen(open); if (!open) setSettingsOrgId(null); }}>
            <DialogContent
              className="flex flex-col p-0 overflow-hidden"
              style={{
                maxWidth: 'min(96rem, 90vw)',
                width: '90vw',
                height: '90vh',
                top: '5vh',
                left: '50%',
                transform: 'translateX(-50%)',
              }}
            >
              <DialogHeader className="sr-only">
                <DialogTitle>Settings</DialogTitle>
              </DialogHeader>
              <div className="flex-1 flex flex-col overflow-hidden min-h-0">
                <div className="border-b p-4 shrink-0">
                  <div className="flex items-center justify-between mb-4">
                    <h2 className="text-xl font-semibold flex items-center gap-2">
                      <SettingsIcon className="h-5 w-5" />
                      Settings
                    </h2>
                  </div>
                  <div className="relative">
                    <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                    <Input placeholder="Search settings..." className="pl-9" />
                  </div>
                </div>
                <Tabs
                  value={settingsTab}
                  onValueChange={(v) => { setSettingsTab(v); if (v !== 'organizations') setSettingsOrgId(null); }}
                  className="flex-1 flex flex-col overflow-hidden min-h-0"
                >
                  <TabsList className="w-full justify-start border-b rounded-none px-4 shrink-0">
                    <TabsTrigger value="account">Account</TabsTrigger>
                    <TabsTrigger value="llm">AI & LLM</TabsTrigger>
                    <TabsTrigger value="integrations">Integrations</TabsTrigger>
                    <TabsTrigger value="ui">UI Preferences</TabsTrigger>
                    <TabsTrigger value="features">Features</TabsTrigger>
                    <TabsTrigger value="organizations">Workspaces</TabsTrigger>
                  </TabsList>
                  {settingsTab === 'organizations' && (
                    <div className="border-b px-4 py-2 text-sm text-muted-foreground flex items-center gap-1 shrink-0">
                      {settingsOrgId ? (
                        <>
                          <button
                            type="button"
                            className="hover:text-foreground transition-colors"
                            onClick={() => setSettingsOrgId(null)}
                          >
                            Workspaces
                          </button>
                          <ChevronRight className="h-4 w-4 shrink-0" />
                          <span className="text-foreground font-medium">
                            {settingsOrgId === 'new' ? 'New workspace' : (orgs.find((o) => o.id === settingsOrgId)?.name ?? 'Workspace')}
                          </span>
                        </>
                      ) : (
                        <span className="text-muted-foreground">Workspaces</span>
                      )}
                    </div>
                  )}
                  <div className="flex-1 overflow-auto min-h-0">
                    <TabsContent value="account" className="p-6 mt-0">
                      <p className="text-sm text-muted-foreground">Account settings placeholder.</p>
                    </TabsContent>
                    <TabsContent value="llm" className="p-6 mt-0">
                      <p className="text-sm text-muted-foreground">AI & LLM placeholder.</p>
                    </TabsContent>
                    <TabsContent value="integrations" className="p-6 mt-0">
                      <p className="text-sm text-muted-foreground">Integrations placeholder.</p>
                    </TabsContent>
                    <TabsContent value="ui" className="p-6 mt-0">
                      <p className="text-sm text-muted-foreground">UI Preferences placeholder.</p>
                    </TabsContent>
                    <TabsContent value="features" className="p-6 mt-0">
                      <p className="text-sm text-muted-foreground">Features placeholder.</p>
                    </TabsContent>
                    <TabsContent value="organizations" className="p-6 mt-0">
                      {settingsOrgId === 'new' ? (
                        <div className="space-y-6 max-w-2xl">
                          <div>
                            <h3 className="text-lg font-semibold mb-1">New organization</h3>
                            <p className="text-sm text-muted-foreground">Add a new shared organization.</p>
                          </div>
                          <div className="space-y-4 rounded-lg border p-4">
                            <div>
                              <Label htmlFor="org-name">Name</Label>
                              <Input
                                id="org-name"
                                placeholder="Acme Corp"
                                className="mt-2"
                                value={newOrgName}
                                onChange={(e) => {
                                  setNewOrgName(e.target.value);
                                  if (!newOrgSlug) setNewOrgSlug(e.target.value.toLowerCase().replace(/\s+/g, '-'));
                                }}
                              />
                            </div>
                            <div>
                              <Label htmlFor="org-slug">Slug (optional)</Label>
                              <Input
                                id="org-slug"
                                placeholder="acme-corp"
                                className="mt-2"
                                value={newOrgSlug}
                                onChange={(e) => setNewOrgSlug(e.target.value)}
                              />
                            </div>
                            <div>
                              <Label>Type</Label>
                              <div className="flex flex-wrap gap-2 mt-2">
                                {ORG_TYPES.filter((t) => t.value !== 'personal').map(({ value, label }) => (
                                  <Button
                                    key={value}
                                    type="button"
                                    variant={newOrgType === value ? 'default' : 'outline'}
                                    size="sm"
                                    onClick={() => setNewOrgType(value)}
                                  >
                                    {label}
                                  </Button>
                                ))}
                              </div>
                            </div>
                            <div>
                              <Label>Icon</Label>
                              <div className="flex flex-wrap gap-2 mt-2">
                                {ORG_ICONS.map(({ value, Icon, label }) => (
                                  <Button
                                    key={value}
                                    type="button"
                                    variant={newOrgIcon === value ? 'default' : 'outline'}
                                    size="icon"
                                    title={label}
                                    onClick={() => setNewOrgIcon(value)}
                                  >
                                    <Icon className="h-4 w-4" />
                                  </Button>
                                ))}
                              </div>
                            </div>
                            <div>
                              <Label>Color</Label>
                              <div className="flex flex-wrap gap-2 mt-2">
                                {ORG_COLORS.map(({ value, label }) => (
                                  <button
                                    key={value}
                                    type="button"
                                    title={label}
                                    className={`w-8 h-8 rounded-full border-2 transition-transform hover:scale-110 ${
                                      newOrgColor === value ? 'border-foreground ring-2 ring-offset-2 ring-offset-background ring-foreground/30' : 'border-transparent'
                                    }`}
                                    style={{ backgroundColor: value }}
                                    onClick={() => setNewOrgColor(value)}
                                  />
                                ))}
                              </div>
                            </div>
                            <div className="flex gap-2">
                              <Button variant="outline" onClick={() => setSettingsOrgId(null)}>Cancel</Button>
                              <Button onClick={handleCreateOrg} disabled={!newOrgName.trim()}>Create</Button>
                            </div>
                          </div>
                        </div>
                      ) : settingsOrgId ? (
                        (() => {
                          const org = orgs.find((o) => o.id === settingsOrgId);
                          if (!org) return null;
                          return (
                            <div className="space-y-6 max-w-2xl">
                              <div className="flex items-center gap-3">
                                <OrgIndicator org={org} />
                                <div>
                                  <h3 className="text-lg font-semibold">{org.name}</h3>
                                  <p className="text-sm text-muted-foreground">
                                    {org.type === 'personal' ? 'Your private notes' : `${ORG_TYPES.find((t) => t.value === org.type)?.label ?? org.type} · ${org.memberCount} members`}
                                  </p>
                                </div>
                              </div>
                              <div className="space-y-4 rounded-lg border p-4">
                                <h4 className="text-sm font-medium">Icon & color</h4>
                                {org.type !== 'personal' && (
                                  <div>
                                    <Label>Type</Label>
                                    <div className="flex flex-wrap gap-2 mt-2">
                                      {ORG_TYPES.filter((t) => t.value !== 'personal').map(({ value, label }) => (
                                        <Button
                                          key={value}
                                          type="button"
                                          variant={org.type === value ? 'default' : 'outline'}
                                          size="sm"
                                          onClick={() => updateOrgField(org.id, 'type', value)}
                                        >
                                          {label}
                                        </Button>
                                      ))}
                                    </div>
                                  </div>
                                )}
                                <div>
                                  <Label>Icon</Label>
                                  <div className="flex flex-wrap gap-2 mt-2">
                                    {ORG_ICONS.map(({ value, Icon, label }) => (
                                      <Button
                                        key={value}
                                        type="button"
                                        variant={(org.icon ?? 'Building2') === value ? 'default' : 'outline'}
                                        size="icon"
                                        title={label}
                                        onClick={() => updateOrgField(org.id, 'icon', value)}
                                      >
                                        <Icon className="h-4 w-4" />
                                      </Button>
                                    ))}
                                  </div>
                                </div>
                                <div>
                                  <Label>Color</Label>
                                  <div className="flex flex-wrap gap-2 mt-2">
                                    {ORG_COLORS.map(({ value, label }) => (
                                      <button
                                        key={value}
                                        type="button"
                                        title={label}
                                        className={`w-8 h-8 rounded-full border-2 transition-transform hover:scale-110 ${
                                          (org.color ?? ORG_COLORS[0].value) === value ? 'border-foreground ring-2 ring-offset-2 ring-offset-background ring-foreground/30' : 'border-transparent'
                                        }`}
                                        style={{ backgroundColor: value }}
                                        onClick={() => updateOrgField(org.id, 'color', value)}
                                      />
                                    ))}
                                  </div>
                                </div>
                              </div>
                              <p className="text-xs text-muted-foreground">
                                Members, invites, and other org settings (placeholder for prototype).
                              </p>
                            </div>
                          );
                        })()
                      ) : (
                        <div className="space-y-4">
                          <div>
                            <h3 className="text-lg font-semibold mb-1">Workspaces</h3>
                            <p className="text-sm text-muted-foreground mb-4">
                              Create workspaces, manage memberships, accept invites.
                            </p>
                            <h4 className="text-sm font-medium mb-2">My workspaces</h4>
                            <div className="space-y-2">
                              {orgs.map((org) => (
                                <div
                                  key={org.id}
                                  className={`flex items-center justify-between p-3 rounded-lg border ${org.color ? 'border-l-4' : ''}`}
                                  style={org.color ? { borderLeftColor: org.color } : undefined}
                                >
                                  <div className="flex items-center gap-3">
                                    <OrgIndicator org={org} />
                                    <div>
                                      <div className="font-medium">
                                        {org.name}
                                        {org.type === 'personal' && ' (you)'}
                                      </div>
                                      <div className="text-xs text-muted-foreground">
                                        {org.type === 'personal'
                                          ? 'Your private notes'
                                          : `${ORG_TYPES.find((t) => t.value === org.type)?.label ?? org.type} · ${org.memberCount} members · ${org.role}`}
                                      </div>
                                    </div>
                                  </div>
                                  <Button
                                    variant="ghost"
                                    size="icon"
                                    title="Org settings"
                                    onClick={() => setSettingsOrgId(org.id)}
                                  >
                                    <SettingsIcon className="w-4 h-4" />
                                  </Button>
                                </div>
                              ))}
                            </div>
                          </div>
                          <Button
                            className="w-full"
                            variant="outline"
                            onClick={() => {
                              prepareCreateForm();
                              setSettingsOrgId('new');
                            }}
                          >
                            <Plus className="w-4 h-4 mr-2" />
                            Create organization
                          </Button>
                          <div>
                            <h4 className="text-sm font-medium mb-2">Invited / Pending</h4>
                            <div className="p-3 rounded-lg border flex items-center justify-between">
                              <span className="text-sm">{mockPendingInvite.orgName}</span>
                              <div className="flex gap-2">
                                <Button variant="outline" size="sm">Accept</Button>
                                <Button variant="ghost" size="sm">Decline</Button>
                              </div>
                            </div>
                          </div>
                        </div>
                      )}
                    </TabsContent>
                  </div>
                </Tabs>
              </div>
            </DialogContent>
          </Dialog>

          {/* Info */}
          <Card>
            <CardHeader>
              <CardTitle className="text-base">Prototype status</CardTitle>
              <CardDescription>
                Mock data only. Wireframes: docs/design/2026-02-20-organization-management-wireframes.md.
                Full implementation per docs/implementations/core/organization-management/specs/2026-02-20-organization-management-spec.md.
              </CardDescription>
            </CardHeader>
          </Card>
        </div>
      </div>
    </div>
  );
}
